import React from 'react';
import ReactDOM from 'react-dom';
import {
  SaveDialogOverlay,
  SaveDialog,
  SaveDialogRow,
  SaveDialogButtons,
  SaveDialogButton,
  OpenFileButton,
  GeneratingOverlay,
  GeneratingSpinner,
  GeneratingText,
  ShareDialog as ShareDialogStyled,
  ShareOption,
  HelpPopupOverlay,
  HelpPopup,
  HelpPopupHeader,
  HelpPopupTitle,
  CloseButton,
  HelpPopupContent,
  HelpSection,
  HelpSectionTitle,
  HelpSectionContent,
  HelpFooter,
  HelpCloseButton,
  ComponentImage,
  ShortcutTable
} from './EditorStyles';

// Save Dialog Component
export const SaveFileDialog = ({ isOpen, fileName, setFileName, saveFormat, setSaveFormat, onCancel, onSave }) => {
  if (!isOpen) return null;
  
  return (
    <SaveDialogOverlay onClick={(e) => {
      if (e.target === e.currentTarget) onCancel();
    }}>
      <SaveDialog onClick={(e) => e.stopPropagation()}>
        <h3>Save Document</h3>
        <SaveDialogRow>
          <label>File name:</label>
          <input
            type="text"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="Enter file name"
            autoFocus
          />
        </SaveDialogRow>
        <SaveDialogRow>
          <label>Save as type:</label>
          <select value={saveFormat} onChange={(e) => setSaveFormat(e.target.value)}>
            <option value="txt">Text Document (.txt)</option>
            <option value="html">HTML Document (.html)</option>
            <option value="pdf">PDF Document (.pdf)</option>
            <option value="docx">Word Document (.docx)</option>
          </select>
        </SaveDialogRow>
        <SaveDialogButtons>
          <SaveDialogButton onClick={onCancel}>Cancel</SaveDialogButton>
          <SaveDialogButton onClick={onSave}>Save</SaveDialogButton>
        </SaveDialogButtons>
      </SaveDialog>
    </SaveDialogOverlay>
  );
};

// Open File Dialog Component
export const OpenFileDialog = ({ isOpen, onCancel, onOpen, fileInputRef }) => {
  if (!isOpen) return null;
  
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  return (
    <SaveDialogOverlay onClick={(e) => {
      if (e.target === e.currentTarget) onCancel();
    }}>
      <SaveDialog onClick={(e) => e.stopPropagation()}>
        <h3>Open Document</h3>
        <SaveDialogRow>
          <label>Select a file to open:</label>
          <OpenFileButton onClick={triggerFileInput}>
            Choose File
          </OpenFileButton>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            accept=".txt,.html,.htm,.docx"
            onChange={onOpen}
          />
        </SaveDialogRow>
        <SaveDialogRow>
          <small>Supported formats: TXT, HTML, DOCX</small>
        </SaveDialogRow>
        <SaveDialogButtons>
          <SaveDialogButton onClick={onCancel}>Cancel</SaveDialogButton>
        </SaveDialogButtons>
      </SaveDialog>
    </SaveDialogOverlay>
  );
};

// Share Dialog Component
export const ShareDialog = ({ isOpen, onClose, shareLinkRef, shareLink, copyShareLink, shareViaEmail }) => {
  if (!isOpen) return null;
  
  return (
    <SaveDialogOverlay>
      <ShareDialogStyled>
        <h3>Share Document</h3>

        <p style={{ textAlign: 'center', marginBottom: '20px' }}>
          {navigator.share ?
            'Choose how you would like to share this document:' :
            'Your browser doesn\'t support direct sharing. You can use these options instead:'}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {!navigator.share && (
            <>
              <ShareOption onClick={copyShareLink}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 12H16M8 12C8 9.79086 6.20914 8 4 8C1.79086 8 0 9.79086 0 12C0 14.2091 1.79086 16 4 16C6.20914 16 8 14.2091 8 12ZM16 12C16 14.2091 17.7909 16 20 16C22.2091 16 24 14.2091 24 12C24 9.79086 22.2091 8 20 8C17.7909 8 16 9.79086 16 12Z" stroke="#e0e0e0" strokeWidth="2" />
                </svg>
                <span>Copy Link</span>
              </ShareOption>

              <ShareOption onClick={shareViaEmail}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="4" width="20" height="16" stroke="#e0e0e0" strokeWidth="2" />
                  <polyline points="2,4 12,12 22,4" stroke="#e0e0e0" strokeWidth="2" />
                </svg>
                <span>Email</span>
              </ShareOption>
            </>
          )}

          {navigator.share && (
            <ShareOption onClick={() => {
              onClose();
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="18" cy="5" r="3" stroke="#e0e0e0" strokeWidth="2" />
                <circle cx="18" cy="19" r="3" stroke="#e0e0e0" strokeWidth="2" />
                <circle cx="5" cy="12" r="3" stroke="#e0e0e0" strokeWidth="2" />
                <line x1="7.5" y1="10.5" x2="15.5" y2="6.5" stroke="#e0e0e0" strokeWidth="2" />
                <line x1="7.5" y1="13.5" x2="15.5" y2="17.5" stroke="#e0e0e0" strokeWidth="2" />
              </svg>
              <span>Share to Apps</span>
            </ShareOption>
          )}

          <SaveDialogButton
            onClick={onClose}
            style={{ marginTop: '10px' }}
          >
            Cancel
          </SaveDialogButton>
        </div>
      </ShareDialogStyled>
    </SaveDialogOverlay>
  );
};

// Generating Overlay Component
export const GeneratingOverlayDialog = ({ isVisible, message }) => {
  if (!isVisible) return null;

  return (
    <GeneratingOverlay>
      <GeneratingSpinner />
      <GeneratingText>{message}</GeneratingText>
    </GeneratingOverlay>
  );
};

// Recreate Confirmation Dialog
export const RecreateDialog = ({ isOpen, onCancel, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <SaveDialogOverlay onClick={(e) => {
      if (e.target === e.currentTarget) onCancel();
    }}>
      <SaveDialog onClick={(e) => e.stopPropagation()}>
        <h3>Confirm Recreate</h3>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <p>Do you really want to create new summary and recommendations?</p>
          <p style={{ fontSize: '14px', color: '#ff9800', marginTop: '10px' }}>This will replace your current content.</p>
        </div>
        <SaveDialogButtons style={{ justifyContent: 'center', gap: '20px' }}>
          <SaveDialogButton 
            onClick={onCancel} 
            style={{ 
              background: '#333345', 
              color: '#e0e0e0',
              minWidth: '100px'
            }}
          >
            Cancel
          </SaveDialogButton>
          <SaveDialogButton 
            onClick={onConfirm}
            style={{ 
              background: 'linear-gradient(90deg, #FF8C42 0%, #F85E00 100%)', 
              color: 'white',
              minWidth: '100px'
            }}
          >
            Confirm
          </SaveDialogButton>
        </SaveDialogButtons>
      </SaveDialog>
    </SaveDialogOverlay>
  );
};

// Create a container div for the help popup portal
let helpPopupContainer = null;

// Function to get or create help popup container
export const getHelpPopupContainer = () => {
  if (!helpPopupContainer) {
    helpPopupContainer = document.createElement('div');
    helpPopupContainer.id = 'texteditor-help-popup-container';
    document.body.appendChild(helpPopupContainer);
  }
  return helpPopupContainer;
};

// Help Popup Component
export const HelpPopupDialog = ({ isVisible, onClose }) => {
  if (!isVisible) return null;

  const helpPopupContent = (
    <HelpPopupOverlay>
      <HelpPopup>
        <HelpPopupHeader>
          <HelpPopupTitle>Text Editor Help Guide</HelpPopupTitle>
          <CloseButton onClick={onClose}>✕</CloseButton>
        </HelpPopupHeader>
        <HelpPopupContent>
          <HelpSection>
            <HelpSectionTitle>Overview</HelpSectionTitle>
            <HelpSectionContent>
              This Text Editor provides a comprehensive set of formatting and document manipulation options. You can create, edit, format, and save documents in various formats. This help guide explains all available features and how to use them effectively.
            </HelpSectionContent>
          </HelpSection>

          <HelpSection>
            <HelpSectionTitle>File Menu</HelpSectionTitle>
            <HelpSectionContent>
              The File menu contains options for document management:
              <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                <li><strong>New</strong> - Create a new empty document</li>
                <li><strong>Open</strong> - Open an existing document from your computer (supports .txt, .html, and .docx formats)</li>
                <li><strong>Save</strong> - Save the current document</li>
                <li><strong>Save As</strong> - Save the document with a new name or in a different format</li>
                <li><strong>Print</strong> - Print the current document</li>
                <li><strong>Share</strong> - Share the document with others</li>
              </ul>
            </HelpSectionContent>
          </HelpSection>

          <HelpSection>
            <HelpSectionTitle>Text Formatting</HelpSectionTitle>
            <HelpSectionContent>
              <p>The toolbar provides access to text formatting options:</p>
              <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                <li><strong>Font</strong> - Choose from various font families</li>
                <li><strong>Size</strong> - Set text size</li>
                <li><strong>Bold, Italic, Underline</strong> - Apply common text styles</li>
                <li><strong>Text Color</strong> - Change the color of your text</li>
                <li><strong>Highlight</strong> - Add background color behind text</li>
                <li><strong>Alignment</strong> - Align text left, center, right, or justify</li>
                <li><strong>Lists</strong> - Create bulleted or numbered lists</li>
                <li><strong>Indentation</strong> - Adjust paragraph indentation</li>
              </ul>
            </HelpSectionContent>
          </HelpSection>

          <HelpSection>
            <HelpSectionTitle>Images</HelpSectionTitle>
            <HelpSectionContent>
              You can insert images into your document:
              <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                <li><strong>From URL</strong> - Insert an image using its web address (URL)</li>
                <li><strong>From Computer</strong> - Upload and insert an image from your computer</li>
              </ul>
              <p style={{ marginTop: '10px' }}>To manage an image after insertion:</p>
              <ol style={{ marginTop: '5px', paddingLeft: '20px' }}>
                <li>Click on the image to select it (it will be highlighted with a blue outline)</li>
                <li>Use Delete or Backspace keys to remove a selected image</li>
              </ol>
            </HelpSectionContent>
          </HelpSection>

          <HelpSection>
            <HelpSectionTitle>Undo and Redo</HelpSectionTitle>
            <HelpSectionContent>
              <p>You can undo and redo changes to your document:</p>
              <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                <li><strong>Undo (↶)</strong> - Reverts the most recent change (Ctrl+Z)</li>
                <li><strong>Redo (↷)</strong> - Reapplies a change that was undone (Ctrl+Y)</li>
              </ul>
              <p style={{ marginTop: '10px' }}>The editor maintains a history of up to 20 changes that you can navigate through with these commands.</p>
            </HelpSectionContent>
          </HelpSection>

          <HelpSection>
            <HelpSectionTitle>Keyboard Shortcuts</HelpSectionTitle>
            <HelpSectionContent>
              <p>The editor supports various keyboard shortcuts for quick operations:</p>
              <ShortcutTable>
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Shortcut</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Bold</td>
                    <td>Ctrl+B</td>
                  </tr>
                  <tr>
                    <td>Italic</td>
                    <td>Ctrl+I</td>
                  </tr>
                  <tr>
                    <td>Underline</td>
                    <td>Ctrl+U</td>
                  </tr>
                  <tr>
                    <td>Undo</td>
                    <td>Ctrl+Z</td>
                  </tr>
                  <tr>
                    <td>Redo</td>
                    <td>Ctrl+Y</td>
                  </tr>
                  <tr>
                    <td>Save</td>
                    <td>Ctrl+S</td>
                  </tr>
                  <tr>
                    <td>Select All</td>
                    <td>Ctrl+A</td>
                  </tr>
                  <tr>
                    <td>Copy</td>
                    <td>Ctrl+C</td>
                  </tr>
                  <tr>
                    <td>Cut</td>
                    <td>Ctrl+X</td>
                  </tr>
                  <tr>
                    <td>Paste</td>
                    <td>Ctrl+V</td>
                  </tr>
                  <tr>
                    <td>Indent</td>
                    <td>Tab</td>
                  </tr>
                </tbody>
              </ShortcutTable>
            </HelpSectionContent>
          </HelpSection>

          <HelpSection>
            <HelpSectionTitle>Context Menu</HelpSectionTitle>
            <HelpSectionContent>
              <p>Right-click on selected text to access the context menu with these options:</p>
              <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                <li><strong>Cut</strong> - Remove selected text and copy it to clipboard</li>
                <li><strong>Copy</strong> - Copy selected text to clipboard</li>
                <li><strong>Paste</strong> - Insert text from clipboard</li>
                <li><strong>Delete</strong> - Remove selected text</li>
                <li><strong>Select All</strong> - Select all content in the editor</li>
                <li><strong>Bold, Italic, Underline</strong> - Quick formatting options</li>
              </ul>
            </HelpSectionContent>
            <ComponentImage>
              <div style={{ backgroundColor: '#2D2D2D', padding: '8px 0', borderRadius: '6px', width: '180px', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)' }}>
                <div style={{ padding: '8px 12px', color: 'rgba(255, 255, 255, 0.9)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Cut</span>
                  <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)' }}>Ctrl+X</span>
                </div>
                <div style={{ padding: '8px 12px', color: 'rgba(255, 255, 255, 0.9)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Copy</span>
                  <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)' }}>Ctrl+C</span>
                </div>
                <div style={{ padding: '8px 12px', color: 'rgba(255, 255, 255, 0.9)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Paste</span>
                  <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)' }}>Ctrl+V</span>
                </div>
                <div style={{ height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.1)', margin: '4px 0' }}></div>
                <div style={{ padding: '8px 12px', color: 'rgba(255, 255, 255, 0.9)' }}>
                  <span>Delete</span>
                </div>
              </div>
            </ComponentImage>
          </HelpSection>

          <HelpSection>
            <HelpSectionTitle>Autosave</HelpSectionTitle>
            <HelpSectionContent>
              <p>The editor automatically saves your work as you type. You'll see a "Saving..." indicator followed by "Saved" when your changes have been successfully saved to the server.</p>
              <p style={{ marginTop: '10px' }}>If there's an error saving your work, you'll see an "Error saving" message. In this case, try making another small change to trigger the save process again.</p>
            </HelpSectionContent>
          </HelpSection>
        </HelpPopupContent>
        <HelpFooter>
          <HelpCloseButton onClick={onClose}>Close Help</HelpCloseButton>
        </HelpFooter>
      </HelpPopup>
    </HelpPopupOverlay>
  );

  return ReactDOM.createPortal(helpPopupContent, getHelpPopupContainer());
}; 